import React, { useState, useMemo } from 'react';
import { getTodaysFestival, getUpcomingFestivals, getDaysUntilFestival } from '../data/indianFestivals2026';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';

// Animation Components for different festivals
const KitesAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-kite"
        style={{
          left: `${10 + (i * 12)}%`,
          top: `${-20 - (i * 5)}%`,
          animationDelay: `${i * 0.5}s`,
          animationDuration: `${4 + (i % 3)}s`
        }}
      >
        <svg width="40" height="50" viewBox="0 0 40 50" className="drop-shadow-lg">
          <polygon
            points="20,0 40,20 20,40 0,20"
            fill={colors[i % colors.length]}
            stroke="#333"
            strokeWidth="1"
          />
          <line x1="20" y1="40" x2="20" y2="70" stroke={colors[(i + 1) % colors.length]} strokeWidth="2" />
          <path
            d="M18 50 Q20 55 22 50 Q24 55 26 50"
            fill="none"
            stroke={colors[(i + 2) % colors.length]}
            strokeWidth="1.5"
          />
        </svg>
      </div>
    ))}
  </div>
);

const FireworksAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(12)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-firework"
        style={{
          left: `${5 + (i * 8)}%`,
          bottom: '0%',
          animationDelay: `${i * 0.3}s`
        }}
      >
        <div
          className="w-3 h-3 rounded-full animate-pulse"
          style={{ backgroundColor: colors[i % colors.length], boxShadow: `0 0 20px ${colors[i % colors.length]}` }}
        />
      </div>
    ))}
    {[...Array(20)].map((_, i) => (
      <div
        key={`spark-${i}`}
        className="absolute animate-spark"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 60}%`,
          animationDelay: `${Math.random() * 3}s`,
          animationDuration: `${1 + Math.random() * 2}s`
        }}
      >
        <div
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colors[i % colors.length], boxShadow: `0 0 10px ${colors[i % colors.length]}` }}
        />
      </div>
    ))}
  </div>
);

const DiyasAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(10)].map((_, i) => (
      <div
        key={i}
        className="absolute bottom-0 animate-diya-glow"
        style={{
          left: `${5 + (i * 10)}%`,
          animationDelay: `${i * 0.2}s`
        }}
      >
        <div className="relative">
          <div className="w-6 h-8 relative">
            {/* Flame */}
            <div
              className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-3 h-5 rounded-full animate-flame"
              style={{
                background: `linear-gradient(to top, ${colors[0]}, #FF4500, #FFD700)`,
                boxShadow: `0 0 15px ${colors[0]}, 0 0 30px rgba(255, 165, 0, 0.5)`
              }}
            />
            {/* Diya base */}
            <svg viewBox="0 0 24 20" className="w-full h-full">
              <ellipse cx="12" cy="16" rx="10" ry="4" fill="#8B4513" />
              <ellipse cx="12" cy="14" rx="8" ry="3" fill="#CD853F" />
              <ellipse cx="12" cy="12" rx="6" ry="2" fill={colors[1] || '#FFD700'} />
            </svg>
          </div>
        </div>
      </div>
    ))}
  </div>
);

const ColorsAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(30)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-color-splash"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${Math.random() * 2}s`,
          animationDuration: `${2 + Math.random() * 3}s`
        }}
      >
        <div
          className="w-4 h-4 rounded-full opacity-70"
          style={{
            backgroundColor: colors[i % colors.length],
            boxShadow: `0 0 20px ${colors[i % colors.length]}`
          }}
        />
      </div>
    ))}
  </div>
);

const TricolorAnimation = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute inset-0 flex flex-col opacity-20">
      <div className="flex-1 bg-gradient-to-r from-orange-500 to-orange-400 animate-wave" />
      <div className="flex-1 bg-gradient-to-r from-white to-gray-100" />
      <div className="flex-1 bg-gradient-to-r from-green-600 to-green-500 animate-wave" style={{ animationDelay: '0.5s' }} />
    </div>
    {/* Flying birds/planes */}
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-fly-across"
        style={{
          top: `${15 + (i * 15)}%`,
          animationDelay: `${i * 0.8}s`,
          animationDuration: `${3 + i}s`
        }}
      >
        <span className="text-2xl">✈️</span>
      </div>
    ))}
  </div>
);

const FlowersAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-float-down"
        style={{
          left: `${Math.random() * 100}%`,
          top: '-10%',
          animationDelay: `${Math.random() * 4}s`,
          animationDuration: `${4 + Math.random() * 3}s`
        }}
      >
        <span className="text-2xl" style={{ filter: `hue-rotate(${i * 30}deg)` }}>
          {['🌸', '🌺', '🌼', '🌻', '💐'][i % 5]}
        </span>
      </div>
    ))}
  </div>
);

const SnowflakesAnimation = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(20)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-snowfall text-white"
        style={{
          left: `${Math.random() * 100}%`,
          top: '-5%',
          animationDelay: `${Math.random() * 5}s`,
          animationDuration: `${3 + Math.random() * 4}s`,
          fontSize: `${10 + Math.random() * 15}px`
        }}
      >
        ❄️
      </div>
    ))}
    {/* Christmas tree and decorations */}
    <div className="absolute bottom-2 right-2 text-4xl animate-pulse">🎄</div>
    <div className="absolute top-2 right-4 text-2xl animate-bounce">⭐</div>
  </div>
);

const CrescentMoonAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-4 right-8 animate-glow">
      <span className="text-5xl">🌙</span>
    </div>
    {[...Array(15)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-twinkle"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 60}%`,
          animationDelay: `${Math.random() * 3}s`
        }}
      >
        <span className="text-lg">✨</span>
      </div>
    ))}
  </div>
);

const BalloonsAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(10)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-float-up"
        style={{
          left: `${5 + (i * 10)}%`,
          bottom: '-10%',
          animationDelay: `${i * 0.4}s`,
          animationDuration: `${4 + Math.random() * 2}s`
        }}
      >
        <span className="text-3xl">
          {['🎈', '🎈', '🎈', '🎁', '🎉'][i % 5]}
        </span>
      </div>
    ))}
  </div>
);

const LotusAnimation = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-lotus-bloom">
      <span className="text-6xl">🪷</span>
    </div>
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-glow"
        style={{
          left: `${10 + (i * 12)}%`,
          bottom: `${10 + (i % 3) * 5}%`,
          animationDelay: `${i * 0.3}s`
        }}
      >
        <span className="text-2xl opacity-60">🪷</span>
      </div>
    ))}
  </div>
);

const CoinsAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(15)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-coin-fall"
        style={{
          left: `${Math.random() * 100}%`,
          top: '-10%',
          animationDelay: `${Math.random() * 3}s`,
          animationDuration: `${2 + Math.random() * 2}s`
        }}
      >
        <span className="text-2xl">🪙</span>
      </div>
    ))}
    <div className="absolute bottom-4 right-4 animate-pulse">
      <span className="text-4xl">💰</span>
    </div>
  </div>
);

const RakhiAnimation = ({ colors }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-sparkle"
        style={{
          left: `${10 + (i * 12)}%`,
          top: `${20 + (i % 3) * 20}%`,
          animationDelay: `${i * 0.2}s`
        }}
      >
        <span className="text-2xl">✨</span>
      </div>
    ))}
    <div className="absolute top-1/2 right-8 transform -translate-y-1/2 animate-swing">
      <span className="text-4xl">🎀</span>
    </div>
  </div>
);

const PeacockFeatherAnimation = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-4 right-4 animate-sway">
      <span className="text-5xl">🪶</span>
    </div>
    {[...Array(5)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-sway"
        style={{
          right: `${10 + (i * 8)}%`,
          top: `${20 + (i * 10)}%`,
          animationDelay: `${i * 0.3}s`,
          opacity: 0.6
        }}
      >
        <span className="text-2xl">🪶</span>
      </div>
    ))}
    <div className="absolute bottom-4 left-4 animate-pulse">
      <span className="text-3xl">🪈</span>
    </div>
  </div>
);

const SunriseAnimation = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-orange-500/30 via-yellow-400/20 to-transparent animate-sunrise" />
    <div className="absolute bottom-1/4 left-1/2 transform -translate-x-1/2 animate-sun-rise">
      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 shadow-lg" style={{ boxShadow: '0 0 40px rgba(255, 165, 0, 0.6)' }} />
    </div>
    {[...Array(8)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-ray"
        style={{
          left: '50%',
          bottom: '25%',
          transform: `rotate(${i * 45}deg)`,
          transformOrigin: 'bottom center',
          animationDelay: `${i * 0.1}s`
        }}
      >
        <div className="w-1 h-20 bg-gradient-to-t from-yellow-400 to-transparent" />
      </div>
    ))}
  </div>
);

const ModakAnimation = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    <div className="absolute top-4 right-8 animate-bounce">
      <span className="text-4xl">🐘</span>
    </div>
    {[...Array(6)].map((_, i) => (
      <div
        key={i}
        className="absolute animate-float"
        style={{
          left: `${15 + (i * 15)}%`,
          top: `${30 + (i % 2) * 20}%`,
          animationDelay: `${i * 0.3}s`
        }}
      >
        <span className="text-2xl">🍡</span>
      </div>
    ))}
  </div>
);

// Animation selector
const getAnimationComponent = (animationType, colors) => {
  switch (animationType) {
    case 'kites':
      return <KitesAnimation colors={colors} />;
    case 'fireworks':
      return <FireworksAnimation colors={colors} />;
    case 'diyas':
      return <DiyasAnimation colors={colors} />;
    case 'colors':
      return <ColorsAnimation colors={colors} />;
    case 'tricolor':
      return <TricolorAnimation />;
    case 'flowers':
      return <FlowersAnimation colors={colors} />;
    case 'snowflakes':
      return <SnowflakesAnimation />;
    case 'crescentMoon':
      return <CrescentMoonAnimation colors={colors} />;
    case 'balloons':
      return <BalloonsAnimation colors={colors} />;
    case 'lotus':
      return <LotusAnimation />;
    case 'coins':
      return <CoinsAnimation colors={colors} />;
    case 'rakhi':
      return <RakhiAnimation colors={colors} />;
    case 'peacockFeather':
      return <PeacockFeatherAnimation />;
    case 'sunrise':
      return <SunriseAnimation />;
    case 'modak':
      return <ModakAnimation />;
    default:
      return <FlowersAnimation colors={colors} />;
  }
};

const FestivalWidget = () => {
  const [isVisible, setIsVisible] = useState(true);
  const todaysFestival = useMemo(() => getTodaysFestival(), []);
  const upcomingFestivals = useMemo(() => getUpcomingFestivals(7), []);

  // If no festival today and no upcoming festivals, don't render
  if (!todaysFestival && upcomingFestivals.length === 0) {
    return null;
  }

  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 z-50 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
      >
        <span className="text-xl">{todaysFestival?.emoji || '🎉'}</span>
        <span className="text-sm font-medium">Show Festival</span>
      </button>
    );
  }

  // Render today's festival
  if (todaysFestival) {
    return (
      <div className="relative overflow-hidden bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-purple-500/30 animate-fadeIn">
        {/* Close button */}
        <button
          onClick={() => setIsVisible(false)}
          className="absolute top-3 right-3 z-20 text-gray-400 hover:text-white transition-colors bg-gray-800/50 rounded-full p-1"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Animation Background */}
        {getAnimationComponent(todaysFestival.animation, todaysFestival.colors)}

        {/* Content */}
        <div className="relative z-10 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            {/* Festival Icon */}
            <div
              className="flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl flex items-center justify-center text-5xl sm:text-6xl animate-bounce shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${todaysFestival.colors[0]}40, ${todaysFestival.colors[1] || todaysFestival.colors[0]}40)`,
                border: `2px solid ${todaysFestival.colors[0]}60`
              }}
            >
              {todaysFestival.emoji}
            </div>

            {/* Festival Info */}
            <div className="flex-1 text-center sm:text-left">
              <p className="text-sm font-semibold text-purple-400 uppercase tracking-wider mb-1">
                Today's Festival
              </p>
              <h2
                className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 bg-clip-text text-transparent animate-shimmer"
                style={{
                  backgroundImage: `linear-gradient(90deg, ${todaysFestival.colors.join(', ')}, ${todaysFestival.colors[0]})`
                }}
              >
                Happy {todaysFestival.name}!
              </h2>
              <p className="text-gray-300 text-base sm:text-lg max-w-2xl leading-relaxed">
                {todaysFestival.wish}
              </p>
              {todaysFestival.regionalNames && (
                <p className="text-gray-500 text-sm mt-2">
                  Also known as: {todaysFestival.regionalNames.join(', ')}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom gradient border */}
        <div
          className="h-1"
          style={{
            background: `linear-gradient(90deg, ${todaysFestival.colors.join(', ')})`
          }}
        />
      </div>
    );
  }

  // Render upcoming festivals if no festival today
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-xl border border-gray-700/50">
      {/* Close button */}
      <button
        onClick={() => setIsVisible(false)}
        className="absolute top-3 right-3 z-20 text-gray-400 hover:text-white transition-colors bg-gray-800/50 rounded-full p-1"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
            <CalendarDaysIcon className="w-6 h-6 text-white" />
          </div>
          <h3 className="text-xl font-bold text-white">Upcoming Festivals</h3>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {upcomingFestivals.slice(0, 3).map((festival) => {
            const daysUntil = getDaysUntilFestival(festival.date);
            return (
              <div
                key={festival.date}
                className="p-4 rounded-xl bg-gray-800/50 border border-gray-700/50 hover:border-purple-500/50 transition-all hover:scale-105"
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{festival.emoji}</span>
                  <div>
                    <p className="font-semibold text-white text-sm">{festival.name}</p>
                    <p className="text-xs text-purple-400">
                      {daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FestivalWidget;
